import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ageRatio,
  assetDiscipline,
  assetReview,
  assetScore,
  buildingScore,
  checkDefect,
  DECAY_CATEGORY,
  decayIntensity,
  defectRule,
  NO_JUDGEMENT,
  safetyNetOf,
  theoreticalScore,
  todayIso,
  type DefectIntensity,
  type DefectLocation,
  type DefectSeverity,
  type AssessmentDetailDto,
} from "@klantpunt/shared";
import { api } from "../../lib/api";
import { queuePhoto } from "../../lib/photos";
import { queue } from "../../offline/queue";
import { useQueueState } from "../../offline/state";

/**
 * Veldflow conditiemeting (docs/CONDITIEMETING.md §10.2): gebreken en oordelen gaan via de
 * offline-wachtrij (idempotent op het id) en staan meteen in de bewaarde cache, ook zonder bereik.
 */
/** Gebrek zoals de veldflow het verstuurt (zelfde velden als `defectCreateSchema`). */
export interface NewDefect {
  id: string;
  assetId: string;
  catalogDefectId: string | null;
  description: string | null;
  defectLocation: DefectLocation;
  componentNote: string | null;
  severity: DefectSeverity | null;
  intensity: DefectIntensity | null;
  extentPercent: number | null;
  defectCategory?: string | null;
  stackedOnId?: string | null;
}

/** Oordeel en bouwdeel per asset, zoals de veldflow ze verstuurt (`assetScoreUpdateSchema`). */
export interface JudgementBody {
  notInspectable?: boolean;
  inspected?: boolean;
  remarks?: string | null;
  overrideScore?: number | null;
  overrideReason?: string | null;
  catalogPartCode?: string | null;
  catalogPartSpec?: string | null;
  lifespanYears?: number | null;
}

export const assessmentKey = (id: string) => ["condition-assessment", id] as const;

export function useAssessmentDetail(id: string) {
  return useQuery({
    queryKey: assessmentKey(id),
    queryFn: () => api<AssessmentDetailDto>(`/condition/assessments/${id}/detail`),
  });
}

/** Wijzigingen van deze meting die nog op verbinding wachten. */
export function usePendingConditionOps(assessmentId: string) {
  useQueueState();
  return queue
    .mine()
    .filter(
      (op) =>
        op.req.kind === "http" &&
        (op.req.path.startsWith(`/condition/assessments/${assessmentId}/`) ||
          (op.req.path.startsWith("/condition/defects/") &&
            (op.req.body as { assessmentId?: string } | undefined)?.assessmentId === assessmentId)),
    ).length;
}

/**
 * Na een lokale wijziging (ook offline): score volgens NEN 2767-1, verval-voorstel, waarschuwingen en
 * gebouwscore opnieuw bepalen, met dezelfde functies als de server.
 */
export function recompute(d: AssessmentDetailDto): AssessmentDetailDto {
  const net = safetyNetOf(d.assessment.instruction);
  const method = d.assessment.instruction.method ?? "table5";
  const live = d.assessment.status !== "completed";
  const assets = d.assets.map((a) => {
    // Bouwjaar of levensduur kan in de meting veranderen (levensduur uit de lijst overnemen).
    const ratio = live
      ? ageRatio(a.asset.yearBuilt, a.asset.lifespanYears, d.assessment.assessedOn ?? todayIso())
      : a.ageRatio;
    const decay = a.notInspectable ? null : decayIntensity(ratio, net);
    const review = assetReview({
      notInspectable: a.notInspectable,
      inspected: a.inspected,
      defectCount: a.defects.length,
      overrideScore: a.overrideScore,
      yearBuilt: a.asset.yearBuilt,
      lifespanYears: a.asset.lifespanYears,
      hasReplacementValue: (a.asset.replacementValueCents ?? 0) > 0,
      decayMissing: decay != null && !a.defects.some((x) => x.defectCategory === DECAY_CATEGORY),
    });
    const calc = review.judged && !a.notInspectable ? assetScore(a.defects, method) : null;
    return {
      ...a,
      calculatedScore: calc?.score ?? null,
      calculatedOutcome: calc ? Math.round(calc.outcome * 10000) / 10000 : null,
      how: calc?.how ?? null,
      ageRatio: ratio,
      theoreticalScore: live ? theoreticalScore(ratio) : a.theoreticalScore,
      decayIntensity: decay,
      effectiveScore: a.notInspectable ? null : (a.overrideScore ?? calc?.score ?? null),
      warnings: review.warnings,
    };
  });
  return {
    ...d,
    assets,
    open: assets.filter((a) => a.warnings.includes(NO_JUDGEMENT)).length,
    building: buildingScore(
      assets.map((a) => ({
        discipline: assetDiscipline(a.asset.elementCode, a.asset.assetKind),
        score: a.effectiveScore,
        outcome: a.overrideScore == null ? a.calculatedOutcome : null,
        valueCents: a.asset.replacementValueCents,
      })),
    ),
  };
}

function withPhotos(d: AssessmentDetailDto, defectId: string, f: (ps: string[]) => string[]): AssessmentDetailDto {
  return {
    ...d,
    assets: d.assets.map((a) => ({
      ...a,
      defects: a.defects.map((x) => (x.id === defectId ? { ...x, photos: f(x.photos ?? []) } : x)),
    })),
  };
}

export function useConditionField(assessmentId: string, buildingId: string) {
  const qc = useQueryClient();
  const key = assessmentKey(assessmentId);
  const get = () => qc.getQueryData<AssessmentDetailDto>(key);
  const put = (d: AssessmentDetailDto) => qc.setQueryData(key, d);
  const finish = (r: { status: "done"; data: AssessmentDetailDto } | { status: "queued" }) => {
    if (r.status === "done") put(r.data);
    return r.status;
  };

  return {
    /** Gebrek vastleggen; controleert eerst de regels uit §4 (ook offline). */
    async addDefect(body: NewDefect, label: string) {
      const d = get();
      if (!d) throw new Error("Meting niet geladen");
      const item = body.catalogDefectId ? d.catalogItems.find((i) => i.id === body.catalogDefectId) : null;
      const check = checkDefect(body, item ? defectRule(item) : null);
      if (!check.ok) throw new Error(check.message);
      put(
        recompute({
          ...d,
          assets: d.assets.map((a) =>
            a.asset.id !== body.assetId
              ? a
              : {
                  ...a,
                  defects: [
                    ...a.defects,
                    {
                      id: body.id,
                      assetId: body.assetId,
                      catalogDefectId: item?.id ?? null,
                      title:
                        item && item.defectType !== "own" ? item.description : (body.description ?? "Eigen gebrek"),
                      description: body.description ?? null,
                      defectLocation: body.defectLocation,
                      componentNote: body.componentNote ?? null,
                      severity: check.severity,
                      intensity: check.intensity,
                      extentPercent: body.extentPercent ?? null,
                      isOptional: check.isOptional,
                      optionalAspect: item?.optionalAspect ?? null,
                      defectCategory: item?.defectCategory ?? body.defectCategory ?? null,
                      defectCode: item?.defectCode ?? null,
                      stackedOnId: check.isOptional ? null : (body.stackedOnId ?? null),
                      findingId: null,
                      photos: [],
                      createdAt: new Date().toISOString(),
                    },
                  ],
                },
          ),
        }),
      );
      return finish(
        await queue.enqueue<AssessmentDetailDto>({
          id: body.id,
          buildingId,
          label,
          req: { kind: "http", method: "POST", path: `/condition/assessments/${assessmentId}/defects`, body },
        }),
      );
    },
    async removeDefect(defectId: string, label: string) {
      const d = get();
      if (d)
        put(
          recompute({
            ...d,
            assets: d.assets.map((a) => ({
              ...a,
              defects: a.defects
                .filter((x) => x.id !== defectId)
                .map((x) => (x.stackedOnId === defectId ? { ...x, stackedOnId: null } : x)),
            })),
          }),
        );
      return finish(
        await queue.enqueue<AssessmentDetailDto>({
          buildingId,
          label,
          req: { kind: "http", method: "DELETE", path: `/condition/defects/${defectId}`, body: { assessmentId } },
        }),
      );
    },
    /** Foto bij een gebrek: eerst de upload, daarna de koppeling (beide via de wachtrij, in volgorde). */
    async addPhoto(defectId: string, file: File) {
      const fileId = await queuePhoto(file, buildingId);
      const d = get();
      if (d) put(withPhotos(d, defectId, (ps) => [...ps, fileId]));
      return finish(
        await queue.enqueue<AssessmentDetailDto>({
          buildingId,
          label: "Foto bij gebrek",
          req: {
            kind: "http",
            method: "POST",
            path: `/condition/defects/${defectId}/photos`,
            body: { fileId, assessmentId },
          },
        }),
      );
    },
    async removePhoto(defectId: string, fileId: string) {
      const d = get();
      if (d) put(withPhotos(d, defectId, (ps) => ps.filter((x) => x !== fileId)));
      return finish(
        await queue.enqueue<AssessmentDetailDto>({
          buildingId,
          label: "Foto verwijderen",
          req: {
            kind: "http",
            method: "DELETE",
            path: `/condition/defects/${defectId}/photos/${fileId}`,
            body: { assessmentId },
          },
        }),
      );
    },
    /** Oordeel per asset: geen gebreken, niet te inspecteren, correctie met reden, opmerkingen, bouwdeel. */
    async setJudgement(assetId: string, body: JudgementBody, label: string) {
      const d = get();
      if (d)
        put(
          recompute({
            ...d,
            assets: d.assets.map((a) =>
              a.asset.id !== assetId
                ? a
                : {
                    ...a,
                    asset: {
                      ...a.asset,
                      ...(body.catalogPartCode !== undefined
                        ? { catalogPartCode: body.catalogPartCode, catalogPartSpec: body.catalogPartSpec ?? null }
                        : body.catalogPartSpec !== undefined
                          ? { catalogPartSpec: body.catalogPartSpec }
                          : {}),
                      ...(body.lifespanYears !== undefined ? { lifespanYears: body.lifespanYears } : {}),
                    },
                    ...(body.notInspectable !== undefined ? { notInspectable: body.notInspectable } : {}),
                    ...(body.inspected !== undefined ? { inspected: body.inspected } : {}),
                    ...(body.remarks !== undefined ? { remarks: body.remarks ?? null } : {}),
                    ...(body.overrideScore !== undefined
                      ? { overrideScore: body.overrideScore ?? null, overrideReason: body.overrideReason ?? null }
                      : {}),
                  },
            ),
          }),
        );
      return finish(
        await queue.enqueue<AssessmentDetailDto>({
          buildingId,
          label,
          req: { kind: "http", method: "PUT", path: `/condition/assessments/${assessmentId}/assets/${assetId}`, body },
        }),
      );
    },
  };
}
